import { useCallback, useMemo, useState } from 'react';
import { chapters } from '../data/chapters';
import type { Answers, ChapterPhase, FlowStep } from '../types/pulse';

const PHASES: ChapterPhase[] = ['opening', 'question', 'followup', 'reward'];

const STEPS: FlowStep[] = [
  { kind: 'intro' },
  { kind: 'profile' },
  ...chapters.flatMap((_, chapterIndex) =>
    PHASES.map((kind): FlowStep => ({ kind, chapterIndex }))
  ),
  { kind: 'summary' },
];

export function usePulseFlow() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [answers, setAnswers] = useState<Answers>({});
  const [role, setRole] = useState<string | null>(null);

  const step = STEPS[index];

  const next = useCallback(() => {
    setDirection(1);
    setIndex((i) => Math.min(i + 1, STEPS.length - 1));
  }, []);

  const back = useCallback(() => {
    setDirection(-1);
    setIndex((i) => Math.max(i - 1, 0));
  }, []);

  const answerMain = useCallback(
    (chapterId: string, optionId: string) => {
      setAnswers((prev) => {
        const existing = prev[chapterId];
        const keepFollowUp = existing?.main === optionId ? existing.followUp : null;
        return { ...prev, [chapterId]: { main: optionId, followUp: keepFollowUp } };
      });
      next();
    },
    [next]
  );

  const answerFollowUp = useCallback(
    (chapterId: string, optionId: string | null) => {
      setAnswers((prev) => ({
        ...prev,
        [chapterId]: { main: prev[chapterId]?.main ?? null, followUp: optionId },
      }));
      next();
    },
    [next]
  );

  const chooseRole = useCallback(
    (roleId: string | null) => {
      setRole(roleId);
      next();
    },
    [next]
  );

  const restart = useCallback(() => {
    setDirection(-1);
    setAnswers({});
    setRole(null);
    setIndex(0);
  }, []);

  const chapterProgress = useMemo(() => {
    return chapters.map((_, c) => {
      if (step.kind === 'summary') return 1;
      if (step.kind === 'intro' || step.kind === 'profile') return 0;
      if (step.chapterIndex > c) return 1;
      if (step.chapterIndex < c) return 0;
      return (PHASES.indexOf(step.kind) + 1) / PHASES.length;
    });
  }, [step]);

  return {
    step,
    stepIndex: index,
    direction,
    answers,
    role,
    chapterProgress,
    canGoBack: index > 1 && step.kind !== 'summary',
    next,
    chooseRole,
    back,
    answerMain,
    answerFollowUp,
    restart,
  };
}
